'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const TermsConditionsInteractive = () => {
  const sections = [
    {
      id: 'aceptacion',
      title: '1. Aceptación y alcance',
      content: `Al registrarte, acceder o utilizar LynMet, aceptas estos Términos y Condiciones. Si no estás de acuerdo con ellos, no debes usar la plataforma.

Estos términos aplican a usuarios gratuitos, usuarios con plan de pago y cualquier persona que interactúe con LynMet a través de sus páginas, formularios o funcionalidades activas.`
    },
    {
      id: 'descripcion-servicio',
      title: '2. Qué es LynMet',
      content: `LynMet es una plataforma de coaching empresarial apoyada por inteligencia artificial. Su objetivo es ayudarte a pensar mejor, ordenar información, detectar brechas, generar ideas, analizar escenarios y construir próximos pasos con mayor claridad.

**La plataforma puede incluir funcionalidades como:**
• Diagnóstico inicial de negocio
• Coaches especializados por tipo de problema
• Historial de sesiones y materiales generados
• Análisis, resúmenes, preguntas estratégicas y planes de trabajo
• Suscripciones, tokens o recargas cuando esas funciones estén activas

**Naturaleza del servicio:**
• LynMet entrega apoyo, análisis y orientación operativa
• No reemplaza asesoría legal, tributaria, contable, financiera ni de inversión
• La información generada puede contener errores, omisiones o interpretaciones incompletas
• Las decisiones finales siempre son responsabilidad del usuario`
    },
    {
      id: 'registro-cuenta',
      title: '3. Cuenta y acceso',
      content: `Para usar LynMet debes registrarte con información real y mantener tus credenciales protegidas.

**Como usuario, aceptas que:**
• Eres responsable de la actividad realizada desde tu cuenta
• No debes compartir tu acceso con terceros
• Debes mantener tus datos razonablemente actualizados
• Debes avisarnos si detectas uso no autorizado o un problema de seguridad

Podemos suspender o cerrar cuentas si detectamos fraude, abuso, uso ilegal, automatización no autorizada, ataques al sistema o incumplimientos relevantes de estos términos.`
    },
    {
      id: 'pagos-tokens',
      title: '4. Tokens, planes y pagos',
      content: `LynMet puede operar con un sistema de tokens, planes gratuitos, suscripciones o recargas puntuales. Las condiciones visibles dentro de la app al momento de contratar son las que rigen para cada producto activo.

**Reglas generales:**
• Los tokens se usan para ejecutar acciones específicas dentro de la plataforma
• Los saldos, recargas y beneficios pueden variar según el plan vigente
• Los tokens no son transferibles entre usuarios
• Los pagos procesados correctamente activan o acreditan el producto correspondiente según la lógica vigente del sistema

**Reembolsos:**
• No corresponde reembolso por consumo ya realizado dentro de la plataforma
• Si existiera un error técnico atribuible a LynMet que impida el uso del servicio pagado, el caso podrá revisarse individualmente
• Cualquier revisión de cobro o problema de acceso debe informarse por correo a **contacto@lynmet.com**`
    },
    {
      id: 'propiedad-intelectual',
      title: '5. Propiedad intelectual y uso permitido',
      content: `El software, la marca, el diseño, los flujos, la estructura de la plataforma y sus elementos visuales pertenecen a LynMet o se usan con las licencias correspondientes.

**Tu contenido sigue siendo tuyo:**
• La información que ingresas para trabajar tu negocio pertenece al usuario
• Nos autorizas a procesarla únicamente para operar la plataforma y prestar el servicio
• Podremos usar información agregada o anonimizada para mejorar el producto, sin exponer datos personales identificables

**No está permitido:**
• Copiar la plataforma o su lógica para crear un servicio competidor
• Hacer scraping, extracción masiva o automatización no autorizada
• Intentar vulnerar seguridad, límites, reglas de consumo o controles internos`
    },
    {
      id: 'limitacion-responsabilidad',
      title: '6. Límites del servicio y responsabilidad',
      content: `LynMet se ofrece "tal cual" y según disponibilidad razonable. Hacemos esfuerzos por mantener la plataforma operativa, pero no garantizamos ausencia total de errores, interrupciones o resultados específicos.

**Importante:**
• Las recomendaciones, análisis y respuestas generadas por IA no aseguran resultados de negocio
• Debes validar decisiones relevantes con criterio propio y, cuando corresponda, con asesores profesionales
• LynMet no responde por pérdidas indirectas, decisiones comerciales fallidas o daños derivados de información interpretada erróneamente por el usuario

En la medida permitida por la ley aplicable, cualquier responsabilidad total de LynMet se limitará al monto efectivamente pagado por el usuario por el servicio directamente relacionado con el reclamo.`
    },
    {
      id: 'conducta-usuario',
      title: '7. Conducta permitida y prohibida',
      content: `Esperamos un uso legítimo, responsable y compatible con la finalidad de la plataforma.

**Está prohibido:**
• Usar LynMet para actividades ilegales, engañosas o fraudulentas
• Intentar acceder a cuentas ajenas o vulnerar seguridad
• Introducir malware, scripts maliciosos o automatizaciones abusivas
• Manipular el sistema de tokens, pagos, límites o consumo
• Revender accesos o redistribuir el servicio sin autorización

El incumplimiento puede implicar suspensión, cierre de cuenta y, si corresponde, acciones legales.`
    },
    {
      id: 'disponibilidad',
      title: '8. Disponibilidad, cambios y mantenimiento',
      content: `Podemos actualizar, ajustar, pausar o descontinuar funciones para mejorar el producto, corregir errores, reforzar seguridad o redefinir el alcance del servicio.

Puede haber mantenimientos programados o interrupciones no previstas. Cuando sea razonable hacerlo, intentaremos avisar cambios relevantes o trabajos que afecten el uso normal de la plataforma.`
    },
    {
      id: 'resolucion-disputas',
      title: '9. Resolución de controversias',
      content: `Ante cualquier conflicto relacionado con LynMet, ambas partes procurarán primero una resolución directa y de buena fe por correo electrónico.

Si eso no fuera suficiente, la controversia se someterá a la jurisdicción competente que corresponda según la estructura legal y operativa vigente de LynMet al momento del conflicto, salvo que una norma imperativa disponga otra cosa.`
    },
    {
      id: 'cambios-terminos',
      title: '10. Cambios a estos términos',
      content: `Podemos modificar estos términos para reflejar cambios del producto, nuevas funcionalidades, obligaciones legales o ajustes operativos.

Cuando corresponda, actualizaremos la fecha de revisión y, si el cambio es relevante, podremos comunicarlo dentro de la plataforma o por correo. El uso continuado de LynMet después de esos cambios implica aceptación de la nueva versión.`
    },
    {
      id: 'contacto',
      title: '11. Contacto',
      content: `Si tienes dudas sobre estos Términos y Condiciones o necesitas escribirnos por cualquier asunto relacionado con LynMet, contáctanos en:

• Correo: **contacto@lynmet.com**
• Canal: consultas generales, soporte y temas relacionados con la plataforma
• Tiempo de respuesta: haremos esfuerzos razonables por responder a la brevedad`
    }
  ];

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(44,120,251,0.08),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(62,224,190,0.12),transparent_32%),linear-gradient(180deg,#f7fbff_0%,#ffffff_48%,#eefcf8_100%)]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link href="/" className="flex items-center space-x-2 group">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundImage: 'var(--gradient-primary-action)' }}>
                <Icon name="SparklesIcon" size={24} className="text-white" />
              </div>
              <span className="text-2xl font-black text-gray-900">LynMet</span>
            </Link>
            
            <Link
              href="/"
              className="flex items-center space-x-2 text-gray-600 hover:text-[var(--primary)] transition-colors"
            >
              <Icon name="ArrowLeftIcon" size={20} />
              <span className="font-medium">Volver al Inicio</span>
            </Link>
          </div>
        </div>
      </header>
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-80 h-80 bg-[#2c78fb]/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#3ee0be]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '700ms' }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="section-eyebrow mb-6">
            <Icon name="DocumentTextIcon" size={20} className="text-[var(--primary)]" />
            <span className="text-sm font-semibold">
              Última actualización: Marzo 2026
            </span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-black text-gray-900 mb-6">
            Términos y Condiciones
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Reglas claras para usar LynMet de forma responsable, entendiendo qué hace la plataforma, qué no promete y cómo se regula tu uso.
          </p>
        </div>
      </section>
      {/* Content Sections */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <div className="app-card rounded-3xl overflow-hidden">
          <div className="p-8 lg:p-12 space-y-12">
            {sections?.map((section, index) => (
              <div key={section?.id} id={section?.id} className="scroll-mt-20">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold mr-4" style={{ backgroundImage: 'var(--gradient-primary-action)' }}>
                    {index + 1}
                  </span>
                  {section?.title?.replace(/^\d+\.\s*/, '')}
                </h2>
                <div className="prose prose-lg max-w-none pl-14">
                  {section?.content?.split('\n\n')?.map((paragraph, idx) => {
                    if (paragraph?.startsWith('**') && paragraph?.endsWith('**')) {
                      return (
                        <h3 key={idx} className="text-xl font-bold text-gray-800 mt-6 mb-3">
                          {paragraph?.replace(/\*\*/g, '')}
                        </h3>
                      );
                    }
                    if (paragraph?.startsWith('•')) {
                      return (
                        <ul key={idx} className="list-none space-y-2 my-4">
                          {paragraph?.split('\n')?.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start">
                              <Icon name="CheckCircleIcon" size={20} className="text-[var(--primary)] mr-3 mt-1 flex-shrink-0" />
                              <span className="text-gray-700">{item?.replace('• ', '')}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p key={idx} className="text-gray-700 leading-relaxed mb-4">
                        {paragraph}
                      </p>
                    );
                  })}
                </div>
                {index < sections?.length - 1 && (
                  <div className="mt-8 border-b border-gray-200" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-12 rounded-3xl shadow-2xl p-8 lg:p-12 text-center text-white" style={{ backgroundImage: 'linear-gradient(135deg, #06213f 0%, #0f3f87 34%, #2c78fb 68%, #3ee0be 100%)' }}>
          <div className="max-w-2xl mx-auto">
            <Icon name="RocketLaunchIcon" size={48} className="text-white mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              Usa LynMet con claridad desde el primer paso
            </h2>
            <p className="text-xl text-white/95 mb-8">
              Si quieres empezar hoy, crea tu cuenta y comienza a trabajar tus decisiones con una plataforma pensada para emprendedores que necesitan foco real.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/register"
                className="btn-primary-app group inline-flex items-center justify-center space-x-2 px-8 py-4 text-lg"
              >
                <span>Crear Cuenta Gratis</span>
                <Icon name="ArrowRightIcon" size={22} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/ai-business-coach"
                className="btn-secondary-app-dark inline-flex items-center justify-center space-x-2 px-8 py-4 text-lg"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={22} />
                <span>Explorar Coach IA</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundImage: 'var(--gradient-primary-action)' }}>
              <Icon name="SparklesIcon" size={20} className="text-white" />
            </div>
            <span className="text-xl font-black text-white">LynMet</span>
          </div>
          <p className="text-sm mb-4">
            Coach empresarial impulsado por IA para emprendedores latinoamericanos.
          </p>
          <div className="flex justify-center space-x-6 text-sm">
            <Link href="/privacy-policy" className="hover:text-white transition-colors">
              Privacidad
            </Link>
            <Link href="/terms-conditions" className="hover:text-white transition-colors">
              Términos
            </Link>
            <Link href="/" className="hover:text-white transition-colors">
              Inicio
            </Link>
          </div>
          <p className="text-xs mt-4 text-gray-500">
            © {new Date()?.getFullYear()} LynMet. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditionsInteractive;
